/* La Sura - cake shapes for the preview drawing (after -parts.js, before
   lasura-cake-draw.js). Every shape is a radius at each of N angles round
   the centre (1 = a round cake's radius; angle 0 is right, a quarter turn is
   the front), so one drawing covers them all and a shape morphs by blending
   two lists. Square and rectangle are turned so two sides show, corners on
   sample angles so they stay crisp. Number or letter: -glyph.js. s.spin turns
   the cake about its middle (lasura-cake-turn.js). */
(() => {
  const { f1, esc } = window.LasuraCakeParts;
  const N = 96, TAU = Math.PI * 2, STEP = TAU / N, f2 = (v) => Math.round(v * 100) / 100;
  const cache = {};

  /* A polygon's corner points as a radius per angle. */
  const polar = (pts) => {
    const out = [];
    for (let i = 0; i < N; i++) {
      const dx = Math.cos(i * STEP), dy = Math.sin(i * STEP);
      let best = 0;
      for (let j = 0; j < pts.length; j++) {
        const [x1, y1] = pts[j], [x2, y2] = pts[(j + 1) % pts.length];
        const ex = x2 - x1, ey = y2 - y1, den = dx * ey - dy * ex;
        if (Math.abs(den) < 1e-9) continue;
        const t = (x1 * ey - y1 * ex) / den, u = (x1 * dy - y1 * dx) / den;
        if (t > 0 && u >= -1e-6 && u <= 1 + 1e-6) best = Math.max(best, t);
      }
      out.push(best || 1);
    }
    return out;
  };

  /* A box hw by hd, turned by a whole number of sample steps. */
  const box = (hw, hd, steps) => {
    const a = steps * STEP, c = Math.cos(a), s = Math.sin(a);
    const r = polar([[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]].map(([x, y]) => [x * c - y * s, x * s + y * c]));
    const k = Math.round(Math.atan2(hd, hw) / STEP);
    r.corners = [steps + k, steps - k, steps + N / 2 + k, steps + N / 2 - k].map((i) => (i + N) % N);
    return r;
  };

  /* The classic heart curve, point to the front. */
  const heart = () => {
    const pts = [];
    for (let i = 0; i < 180; i++) {
      const t = (i / 180) * TAU;
      pts.push([(16 * Math.sin(t) ** 3) / 16.5, (13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)) / -16.5]);
    }
    const r = polar(pts);
    r.corners = [N / 4];
    return r;
  };

  function radii(shape) {
    const key = shape === 'square' || shape === 'heart' || shape === 'rectangle' ? shape : 'round';
    if (!cache[key]) {
      /* Square: 8 inch reads a little smaller than it is so the corners fit
         the stand. Rectangle: 3 by 2, as the kitchen bakes it (8 by 12). */
      if (key === 'square') cache[key] = box(0.84, 0.84, 6);
      else if (key === 'rectangle') cache[key] = box(1.1, 0.735, 5);
      else if (key === 'heart') cache[key] = heart();
      else cache[key] = Object.assign(Array(N).fill(1), { corners: [] });
    }
    return cache[key];
  }

  /* The outline in screen space, part-way between two shapes while it morphs.
     Returns what lasura-cake-draw.js needs to draw any shape the same way. */
  function geo(s, cx, rx, ry) {
    const to = radii(s.shape), m = s.shapeFrom == null || s.morph == null ? 1 : s.morph;
    const from = radii(s.shapeFrom);
    const B = m >= 1 ? to : to.map((r, i) => from[i] + (r - from[i]) * m);
    /* Turned by s.spin: the radius at each angle is the unturned shape's a
       little way back (between samples, so a turn can be as small as you
       like); corners move to the nearest sample. */
    const spin = s.spin || 0, sh = spin / STEP;
    const bAt = (f) => {
      const g = ((f % N) + N) % N, i = Math.floor(g);
      return B[i] + (B[(i + 1) % N] - B[i]) * (g - i);
    };
    const R = sh ? B.map((_, i) => bAt(i - sh)) : B;
    const turn = (list) => list.map((i) => ((Math.round(i + sh) % N) + N) % N);
    const pt = (i, yc, rr = 1) => [cx + rx * rr * R[i % N] * Math.cos(i * STEP), yc + ry * rr * R[i % N] * Math.sin(i * STEP)];
    let iL = 0, iR = 0;
    R.forEach((r, i) => {
      const x = r * Math.cos(i * STEP);
      if (x < R[iL] * Math.cos(iL * STEP) - 1e-9) iL = i;
      if (x > R[iR] * Math.cos(iR * STEP) + 1e-9) iR = i;
    });
    /* The front of the outline, left to right, and the whole outline. */
    const F = [];
    for (let i = iL; i !== iR; i = (i - 1 + N) % N) F.push(i);
    F.push(iR);
    const all = [...Array(N + 1).keys()];
    const xy = (p) => `${f1(p[0])},${f1(p[1])}`;
    const line = (idx, yc) => idx.map((i) => xy(pt(i, yc))).join('L');
    /* n + 1 points spaced evenly along the front (or all the way round,
       starting phase of the way round, so piping turns with the cake). Each
       carries the sample index it lies on, to tell front from back. */
    const even = (yc, n, round, phase = 0) => {
      const idx = round ? all : F, ps = idx.map((i) => pt(i, yc)), d = [0];
      for (let i = 1; i < ps.length; i++) d.push(d[i - 1] + Math.hypot(ps[i][0] - ps[i - 1][0], ps[i][1] - ps[i - 1][1]));
      const len = d[d.length - 1], out = [];
      for (let k = 0; k <= n; k++) {
        const want = round ? ((((len * k) / n + phase * len) % len) + len) % len : (len * k) / n;
        let j = 1;
        while (j < d.length - 1 && d[j] < want) j++;
        const f = (want - d[j - 1]) / (d[j] - d[j - 1] || 1);
        out.push([ps[j - 1][0] + (ps[j][0] - ps[j - 1][0]) * f, ps[j - 1][1] + (ps[j][1] - ps[j - 1][1]) * f, idx[j - 1] % N]);
      }
      return Object.assign(out, { len });
    };
    const ext = Math.max(...R.map((r, i) => Math.abs(r * Math.cos(i * STEP))));
    /* Corners arriving and (mid-morph) leaving, shaded by progress, so sides
       fade with the outline instead of popping in at the end. */
    const onFront = (list) => list.filter((i) => F.includes(i) && i !== iL && i !== iR);
    const corners = onFront(turn(to.corners)), cornersFrom = m >= 1 ? [] : onFront(turn(from.corners)), Fs = new Set(F);

    /* Piped writing: each letter sits on the outline at height yc, spaced by
       true distance round the cake and squeezed where the side turns away,
       so it wraps round a round cake and lies flat on a square's widest side.
       A heart's point would crush letters, so there it follows a smooth curve. */
    const write = (msg, yc, size, from = -1) => {
      const flat = (k) => k === 'square' || k === 'rectangle';
      const heart = (s.shape === 'heart' ? m : 0) + (m < 1 && s.shapeFrom === 'heart' ? 1 - m : 0);
      const r = (i) => R[i] + (R[N / 4] - R[i]) * heart;
      const plan = F.map((i) => [rx * r(i) * Math.cos(i * STEP), rx * r(i) * Math.sin(i * STEP)]), d = [0];
      const on = (k) => [cx + plan[k][0], yc + (plan[k][1] * ry) / rx];
      for (let i = 1; i < plan.length; i++) d.push(d[i - 1] + Math.hypot(plan[i][0] - plan[i - 1][0], plan[i][1] - plan[i - 1][1]));
      const len = d[d.length - 1], chars = [...msg];
      /* The whole front or one flat side; mid-morph it glides between. */
      const front = [d[plan.reduce((b, p, i) => (p[1] > plan[b][1] ? i : b), 0)], len * 0.11, len * 0.89];
      const side = (list) => {
        const cut = [0, ...list.map((i) => F.indexOf(i)).sort((a, b) => a - b), F.length - 1];
        let wide = 0, best = front;
        for (let j = 1; j < cut.length; j++) {
          const w = plan[cut[j]][0] - plan[cut[j - 1]][0], a = d[cut[j - 1]], b = d[cut[j]];
          if (w > wide) (wide = w), (best = [(a + b) / 2, a + (b - a) * 0.08, b - (b - a) * 0.08]);
        }
        return best;
      };
      const goal = flat(s.shape) && corners.length ? side(corners) : front;
      const start = m < 1 && flat(s.shapeFrom) ? side(onFront(turn(from.corners))) : front;
      const [mid, lo, hi] = goal.map((v, i) => start[i] + (v - start[i]) * Math.min(1, m));
      const total = window.LasuraCakeMessage.ems(msg), fs = Math.min(size, (hi - lo) / (total || 1));
      /* Turned, the words travel round with the side and leave the front. */
      let at = Math.max(lo, Math.min(hi - total * fs, mid - (total * fs) / 2)) - spin * rx, out = '';
      chars.forEach((ch, n) => {
        const w = window.LasuraCakeMessage.em(ch) * fs, want = at + w / 2;
        at += w;
        if (want < 0 || want > len) return;
        let j = 1;
        while (j < d.length - 1 && d[j] < want) j++;
        const f = (want - d[j - 1]) / (d[j] - d[j - 1] || 1), [x1, y1] = on(j - 1), [x2, y2] = on(j), ds = d[j] - d[j - 1] || 1;
        /* From index from on, just typed: piped on one after another (preview CSS). */
        if (ch !== ' ') out += `<text${from >= 0 && n >= from ? ` class="is-new" style="animation-delay:${(n - from) * 30}ms"` : ''} transform="matrix(${f2((x2 - x1) / ds)} ${f2((y2 - y1) / ds)} 0 1 ${f1(x1 + (x2 - x1) * f)} ${f1(y1 + (y2 - y1) * f)})" font-size="${f1(fs)}" text-anchor="middle">${esc(ch)}</text>`;
      });
      return out;
    };
    return {
      F, pt, xy, line, even, ext, corners, cornersFrom, m, write,
      /* For toppings: a point inside the top, rr of the way out at angle t
         on the cake (turned with it). front(t): whether that angle faces us. */
      on: (t, yc, rr = 1) => {
        const a = t + spin, k = rr * bAt(t / STEP);
        return [cx + rx * k * Math.cos(a), yc + ry * k * Math.sin(a)];
      },
      front: (t) => Fs.has(((Math.round((t + spin) / STEP) % N) + N) % N),
      top: (yc) => `M${line(all.slice(0, N), yc)}Z`,
      body: (top, base) => `M${line(F, base)}L${line([...F].reverse(), top)}Z`
    };
  }

  window.LasuraCakeShape = { radii, geo };
})();
